/**
 * Injection-molding machine domain model for the CMT Real Time Process
 * Monitoring dashboard.
 *
 * A `MachineShot` is one injection cycle ("模次") with its measured process
 * parameters. The numeric parameters are enumerated once in `PARAM_META`
 * (label / unit / group) so the KPI cards, trend charts and detail table can
 * all be driven from a single source of truth. `shotTime` is an epoch-ms
 * timestamp and `moldName` is a free-form string.
 */

/** Logical grouping used to lay out KPI cards and the detail table. */
export type ParamGroup = 'pressure' | 'temperature' | 'timing' | 'position';

/** Every numeric process parameter captured per shot. */
export type ParamKey =
  // Pressure
  | 'injectionPressure'
  | 'peakPressure'
  | 'holdingPressure'
  | 'backPressure'
  // Temperature
  | 'meltTemp'
  | 'nozzleTemp'
  | 'moldTempFront'
  | 'moldTempRear'
  // Timing
  | 'cycleTime'
  | 'injectionTime'
  | 'holdingTime'
  | 'coolingTime'
  // Position
  | 'shotSize'
  | 'cushion'
  | 'switchoverPosition'
  | 'screwPosition';

/** Non-numeric metadata attached to every shot record. */
export interface ShotMeta {
  /** Stable, monotonic unique id (also used as a React key). */
  id: number;
  /** Owning machine id. */
  machineId: string;
  /** Sequential shot number ("模次"). */
  shotNo: number;
  /** Cycle timestamp, epoch milliseconds. */
  shotTime: number;
  /** Mold currently loaded on the machine. */
  moldName: string;
}

/** A single injection cycle: metadata + all numeric process parameters. */
export type MachineShot = ShotMeta & Record<ParamKey, number>;

/** Operational status of a machine (drives the status light). */
export type MachineStatus = 'running' | 'idle' | 'down';

/** An injection-molding machine shown in the selector. */
export interface Machine {
  /** Stable machine id used to key data streams. */
  id: string;
  /** Human-facing machine number, e.g. "IMM-01". */
  machineNo: string;
  /** Descriptive machine name. */
  name: string;
  /** Placeholder / photo shown on the machine card. */
  image: string;
  /** Mold loaded by default (seeds the mock stream). */
  defaultMoldName: string;
  /** Current operational status. */
  status: MachineStatus;
}

/**
 * The two CMT injection-molding machines.
 * `image` points at the machine photo under `public/machines/`.
 */
export const CMT_MACHINES: Machine[] = [
  {
    id: 'cmt-imm-01',
    machineNo: 'IMM-01',
    name: 'Injection Molding Cell #1',
    image: '/machines/cmt-injection-machine.png',
    defaultMoldName: 'Housing-A / 2-cavity',
    status: 'running',
  },
  {
    id: 'cmt-imm-02',
    machineNo: 'IMM-02',
    name: 'Injection Molding Cell #2',
    image: '/machines/cmt-injection-machine.png',
    defaultMoldName: 'Bracket-B / 4-cavity',
    status: 'running',
  },
];

/** Descriptor for one numeric parameter. */
export interface ParamMeta {
  key: ParamKey;
  label: string;
  unit: string;
  group: ParamGroup;
}

/**
 * Metadata for every numeric parameter. Units are best-guess process units for
 * a typical injection-molding machine.
 */
export const PARAM_META: ParamMeta[] = [
  // --- Pressure ---
  { key: 'injectionPressure', label: 'Injection Pressure', unit: 'bar', group: 'pressure' },
  { key: 'peakPressure', label: 'Peak Pressure', unit: 'bar', group: 'pressure' },
  { key: 'holdingPressure', label: 'Holding Pressure', unit: 'bar', group: 'pressure' },
  { key: 'backPressure', label: 'Back Pressure', unit: 'bar', group: 'pressure' },
  // --- Temperature ---
  { key: 'meltTemp', label: 'Melt Temperature', unit: '°C', group: 'temperature' },
  { key: 'nozzleTemp', label: 'Nozzle Temperature', unit: '°C', group: 'temperature' },
  { key: 'moldTempFront', label: 'Mold Temp (Front)', unit: '°C', group: 'temperature' },
  { key: 'moldTempRear', label: 'Mold Temp (Rear)', unit: '°C', group: 'temperature' },
  // --- Timing ---
  { key: 'cycleTime', label: 'Cycle Time', unit: 's', group: 'timing' },
  { key: 'injectionTime', label: 'Injection Time', unit: 's', group: 'timing' },
  { key: 'holdingTime', label: 'Holding Time', unit: 's', group: 'timing' },
  { key: 'coolingTime', label: 'Cooling Time', unit: 's', group: 'timing' },
  // --- Position ---
  { key: 'shotSize', label: 'Shot Size', unit: 'mm', group: 'position' },
  { key: 'cushion', label: 'Cushion', unit: 'mm', group: 'position' },
  { key: 'switchoverPosition', label: 'V/P Switchover', unit: 'mm', group: 'position' },
  { key: 'screwPosition', label: 'Screw Position', unit: 'mm', group: 'position' },
];

/** Ordered groups with display labels (drives dashboard section order). */
export const PARAM_GROUPS: { id: ParamGroup; label: string }[] = [
  { id: 'pressure', label: 'Pressure' },
  { id: 'temperature', label: 'Temperature' },
  { id: 'timing', label: 'Timing' },
  { id: 'position', label: 'Position' },
];

/** All parameter descriptors belonging to a given group, in declared order. */
export function paramsByGroup(group: ParamGroup): ParamMeta[] {
  return PARAM_META.filter((p) => p.group === group);
}

/** Look up a single parameter's metadata by key. */
export function paramMeta(key: ParamKey): ParamMeta | undefined {
  return PARAM_META.find((p) => p.key === key);
}
